import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Chip from "@mui/material/Chip";
import Grow from "@mui/material/Grow";
import Typography from "@mui/material/Typography";
import { styled, type Theme } from "@mui/material/styles";
import SearchBar from "messaging/_views/components/message-search-bar";
import type { ReactNode } from "react";

/**
 * The parts every Correspondence pane is built from, top to bottom: the tab row, the bordered box with
 * the search header and the list under it, and the composer under the box.
 *
 * The work agreement's chat window and the proposal's comment pane both draw from these, so the two
 * panes keep one shape. They used to be two layouts that only resembled each other: the proposal's had
 * no tabs, no search and no files, and an "External Comment" switch in their place.
 */

/** The open conversation tab and the search header under it. The list under them is white. */
export const CORRESPONDENCE_HEADER_COLOR = "#f8f9fd";

/** Three rows: tabs, list, composer. The list takes what the other two leave. */
export const CorrespondenceFrame = styled("div", {
  shouldForwardProp: (prop) => prop !== "fillAvailableSpace",
})<{ fillAvailableSpace?: boolean }>(({ theme, fillAvailableSpace }) => ({
  display: "grid",
  flex: fillAvailableSpace ? "1 1 auto" : undefined,
  gridTemplateRows: "auto minmax(0, 1fr) auto",
  height: fillAvailableSpace ? "100%" : `calc(100% - ${theme.spacing(2)})`,
  minHeight: fillAvailableSpace ? 0 : "75vh",
  minWidth: 0,
  overflow: fillAvailableSpace ? "hidden" : undefined,
}));

/**
 * The bordered box under the tab row: the search header across its full width, the list under it with
 * the date scrubber over its edge, and the jump chip at its foot.
 */
export const CorrespondenceBody = styled("div", {
  shouldForwardProp: (prop) => prop !== "formattingEnabled",
})<{ formattingEnabled?: boolean }>(({ formattingEnabled, theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: "1px solid #aaa",
  // The box joins the tab row above it and the editor's formatting toolbar below it, so it drops its
  // upper edge and squares its lower corners while formatting is open.
  borderBottomLeftRadius: formattingEnabled ? 0 : theme.shape.borderRadius,
  borderBottomRightRadius: formattingEnabled ? 0 : theme.shape.borderRadius,
  borderTop: "none",
  boxSizing: "border-box",
  display: "inline-grid",
  gridRowStart: 2,
  gridTemplateRows: "auto minmax(0, 1fr) auto",
  minHeight: 0,
  position: "relative",
  width: "100%",
}));

/**
 * The inside of the list, which the list's own scroll container takes: the gap down its left side, and
 * room above the first message so it does not sit against the search header. The box itself has no
 * padding, so the header spans it from edge to edge.
 */
export const correspondenceListPadding = (theme: Theme) => ({
  boxSizing: "border-box" as const,
  paddingLeft: theme.spacing(2),
  paddingTop: theme.spacing(1),
});

/** Where the composer goes: under the list, the newest message directly above it. */
export const CorrespondenceComposerSlot = styled("div")(() => ({
  gridRowStart: 3,
  width: "100%",
}));

/**
 * The search header: the box's first row, fixed over the list rather than inside it, across the box's
 * whole width and with room above and below it. It used to sit inside the list, held to the top by
 * `sticky`, as wide as the message column and touching the first message's time (#5839).
 */
export const CorrespondenceSearchBar = styled(SearchBar)(({ theme }) => ({
  // The light blue the open tab above it is drawn in, so the tab and its header read as one piece over
  // the white list.
  backgroundColor: CORRESPONDENCE_HEADER_COLOR,
  borderBottom: `1px solid ${theme.palette.divider}`,
  gridRowStart: 1,
  padding: theme.spacing(1.5, 2),
}));

/** The box's first row: the search header, and under it the audience notice when there is one. */
const HeaderRow = styled("div")(() => ({
  gridRowStart: 1,
  minWidth: 0,
}));

/** One slim line, so it costs a narrow pane as little of its list as it can (#6607). */
const AudienceNotice = styled("p")(({ theme }) => ({
  ...theme.typography.caption,
  alignItems: "center",
  backgroundColor: "#eef1fa",
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  display: "flex",
  gap: theme.spacing(0.75),
  margin: 0,
  padding: theme.spacing(0.5, 2),
}));

const AudienceNoticeIcon = styled(InfoOutlinedIcon)(() => ({
  flexShrink: 0,
  fontSize: "1rem",
}));

/**
 * The box's header: the search, and under it who reads what the reader sends in the open
 * conversation (#6875). Both sit outside the list's scroll container, so the notice stays in view
 * however far the reader scrolls. Without a notice the header is the search alone.
 */
export function CorrespondenceHeader(props: Readonly<{ audienceNotice?: string; children: ReactNode }>) {
  const { audienceNotice, children } = props;
  return (
    <HeaderRow>
      {children}
      {audienceNotice && (
        <AudienceNotice role="note" data-testid="correspondence-audience-notice">
          <AudienceNoticeIcon aria-hidden />
          {audienceNotice}
        </AudienceNotice>
      )}
    </HeaderRow>
  );
}

/** What an empty list, or a search that matched nothing, says in the middle of the box. */
export const CorrespondenceEmptyText = styled(Typography)(() => ({
  alignContent: "center",
  alignItems: "center",
  color: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  fontSize: "1.33em",
  height: "100%",
  justifyContent: "center",
  textAlign: "center",
  whiteSpace: "normal",
  width: "100%",
}));

const JumpToBottomContainer = styled("div")(() => ({
  display: "flex",
  height: 0,
  justifyContent: "center",
  position: "relative",
  width: "100%",
  zIndex: 1,
}));
const JumpToBottomChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  bottom: "20px",
  boxShadow: "0 4px 5px 0 rgb(0 0 0 / 14%), 0 1px 10px 0 rgb(0 0 0 / 12%), 0 2px 4px 0 rgb(0 0 0 / 20%)",
  color: "white",
  position: "absolute",
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },
}));

/** The chip that takes a reader who scrolled up back to the newest message. */
export function CorrespondenceJumpToBottom(props: Readonly<{ visible: boolean; onClick: () => void }>) {
  const { visible, onClick } = props;
  return (
    <JumpToBottomContainer>
      <Grow in={visible}>
        <JumpToBottomChip
          label="Jump to latest"
          icon={<ArrowDownwardIcon style={{ color: "white" }} />}
          onClick={onClick}
        />
      </Grow>
    </JumpToBottomContainer>
  );
}
